import React, { useEffect, useState, useCallback } from "react";
import { Search, Package } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["En attente", "Payée", "En préparation", "Expédiée", "Terminée", "Annulée", "Remboursée"];
const STATUS_COLORS = {
  "En attente": "bg-amber-100 text-amber-700",
  "Payée": "bg-emerald-100 text-emerald-700",
  "En préparation": "bg-blue-100 text-blue-700",
  "Expédiée": "bg-indigo-100 text-indigo-700",
  "Terminée": "bg-emerald-100 text-emerald-700",
  "Annulée": "bg-red-100 text-red-700",
  "Remboursée": "bg-brown-dark/10 text-brown-dark/70",
};
const PAY_COLORS = { paid: "text-emerald-600", pending: "text-amber-600", failed: "text-red-500", cancelled: "text-red-500", refunded: "text-brown-dark/60" };

const OrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filter) params.status = filter;
    if (q) params.q = q;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).catch(() => {});
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (o, status) => {
    try {
      await api.put(`/admin/orders/${o.id}/status`, { order_status: status });
      toast.success("Statut mis à jour"); load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <h2 className="font-heading text-2xl text-brown-black">Commandes ({orders.length})</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brown-black/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="orders-search" className="pl-9 pr-3 py-2.5 rounded-full border border-gold/25 bg-white focus:border-gold focus:outline-none text-sm" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="orders-filter" className="px-4 py-2.5 rounded-full border border-gold/25 bg-white focus:border-gold focus:outline-none text-sm font-semibold">
            <option value="">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {orders.length === 0 ? (
        <p className="text-center text-brown-black/50 py-16 flex flex-col items-center gap-3"><Package size={32} className="text-gold/40" /> Aucune commande pour le moment.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gold/15 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ivory text-brown-dark/70 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Commande</th>
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                  <th className="px-4 py-3 font-semibold">Paiement</th>
                  <th className="px-4 py-3 font-semibold">Statut</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <React.Fragment key={o.id}>
                    <tr className="border-t border-gold/10 hover:bg-ivory/50 cursor-pointer" onClick={() => setOpen(open === o.id ? null : o.id)} data-testid={`order-row-${o.order_number}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-brown-dark">{o.order_number}</td>
                      <td className="px-4 py-3">{o.customer_name}<br /><span className="text-brown-black/50 text-xs">{o.customer_email}</span></td>
                      <td className="px-4 py-3 font-semibold text-gold">{o.total.toFixed(2)} {o.currency}</td>
                      <td className={`px-4 py-3 font-semibold ${PAY_COLORS[o.payment_status] || ""}`}>{o.payment_status}</td>
                      <td className="px-4 py-3">
                        <select value={o.order_status} onClick={(e) => e.stopPropagation()} onChange={(e) => changeStatus(o, e.target.value)} data-testid={`order-status-${o.order_number}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold border-0 ${STATUS_COLORS[o.order_status] || ""}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-brown-black/60 text-xs">{new Date(o.created_at).toLocaleString("fr-FR")}</td>
                    </tr>
                    {open === o.id && (
                      <tr className="bg-ivory/60"><td colSpan={6} className="px-6 py-4">
                        <p className="text-xs uppercase tracking-wide text-brown-dark/50 font-semibold mb-2">Articles</p>
                        <ul className="space-y-1">
                          {o.items.map((it, i) => (
                            <li key={i} className="flex justify-between text-brown-black/80">
                              <span>{it.name}{it.variant_label ? ` — ${it.variant_label}` : ""} × {it.qty}</span>
                              <span>{(it.unit_price * it.qty).toFixed(2)} {o.currency}</span>
                            </li>
                          ))}
                        </ul>
                        {o.transaction_id && <p className="text-xs text-brown-black/50 mt-3">Transaction PayPal : {o.transaction_id}</p>}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersAdmin;
